import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
} from "@hex-insights/core";
import { EmptyView } from "../../../../Components/Empty";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { EmailAddressDetailFragment, Person, PersonNameFragment } from "../../../../Utilities";
import { EmailAddressPersonForm } from "../../../Forms";
import { EmailAddressCard } from "../Card";
import { EmailAddressCreatePermissionRequired } from "../PermissionRequired";
import styles from "./styles.module.css";

export type EmailAddressPersonListModalProps = {
	person: Pick<Person, "id"> & PersonNameFragment;
	emailAddresses: Omit<EmailAddressDetailFragment, "person">[];
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function EmailAddressPersonListModal({
	person,
	emailAddresses,
	ifRef,
	onClose,
	className,
	style,
}: EmailAddressPersonListModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<IconContainer color="var(--theme-colors__blue--light)">
						<Icon.EnvelopeSimple color="var(--theme-colors__blue)" size={18} />
					</IconContainer>

					<Heading.H2 noMargin>Email Addresses</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body style={{ display: "flex", flexDirection: "column" }}>
				<EmailAddressEditablePersonList person={person} emailAddresses={emailAddresses} />
			</Modal.Body>
		</Modal>
	);
}

export type EmailAddressEditablePersonListProps = {
	person: Pick<Person, "id"> & PersonNameFragment;
	emailAddresses: Omit<EmailAddressDetailFragment, "person">[];
	noUtilities?: boolean;
};

export function EmailAddressEditablePersonList({
	person,
	emailAddresses,
	noUtilities,
}: EmailAddressEditablePersonListProps) {
	const {
		isModalOpen: isCreateModalOpen,
		openModal: openCreateModal,
		closeModal: closeCreateModal,
	} = Modal.useToggle();

	const emailAddressesWithPerson = React.useMemo(
		() => emailAddresses.map((emailAddress) => ({ ...emailAddress, person })),
		[emailAddresses, person],
	);

	return (
		<React.Fragment>
			<Conditional>
				<If condition={emailAddresses.length === 0}>
					<EmptyView
						IconComponent={Icon.EnvelopeSimple}
						iconSize={54}
						primaryText="Email Addresses"
						secondaryText="No email addresses listed"
					>
						<EmailAddressCreatePermissionRequired>
							<Button variant="link" size="small" onClick={openCreateModal}>
								Add Email Address
							</Button>
						</EmailAddressCreatePermissionRequired>
					</EmptyView>
				</If>
				<Else>
					<Column justify="spaced-start" verticalSpacing="1.25rem">
						<Column justify="spaced-start">
							{emailAddressesWithPerson.map((emailAddress) => (
								<EmailAddressCard
									key={emailAddress.id}
									emailAddress={emailAddress}
									slim
									editable
									noUtilities={noUtilities}
								/>
							))}
						</Column>

						<EmailAddressCreatePermissionRequired>
							<Button variant="link" size="small" onClick={openCreateModal}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Plus size={16} /> Add Email Address
								</Row>
							</Button>
						</EmailAddressCreatePermissionRequired>
					</Column>
				</Else>
			</Conditional>

			<Modal.If condition={isCreateModalOpen}>
				<CreateModal personID={person.id} onClose={closeCreateModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type CreateModalProps = {
	personID: Person["id"];
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function CreateModal({ personID, ifRef, onClose, className, style }: CreateModalProps) {
	const createFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: { personID },
		}),
		[personID],
	);

	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["create-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>New Email Address</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<EmailAddressPersonForm.ControlledCreate formStateArgs={createFormStateArgs} onSuccess={onSuccess} />
			</Modal.Body>
		</Modal>
	);
}
